import { HttpEvent }             from '@angular/common/http';
import { IKeyValueMap }          from '@core/interfaces/common.interfaces';
import { FilesizeShortUnitEnum } from '@utils/helpers/file-utils';
import { Observable }            from 'rxjs';

// ENUMS #############################################################
export enum FileProcessStatusEnum {
	DELETED = 'deleted',
	FAILED = 'failed',
	NOT_ASKED = 'not asked',
	STARTED = 'started',
	SUCCESS = 'success',
	UPLOADING = 'uploading'
}

export enum FileDropTableColumnIdEnum {
	ACTION_DELETE = 'actionDelete',
	FILE_EXTENSION = 'fileExtension',
	FILE_FULL_NAME = 'fileFullName',
	FILE_NAME = 'fileName',
	FILE_SIZE = 'fileSize',
	PROCESS_STATUS = 'processStatus'
}

export enum FileDropErrorIdEnum {
	API = 'api',
	INVALID_NAME = 'invalidFilename',
	MAX_FILESIZE = 'maxFilesize',
}


// TYPES #############################################################

// INTERFACES ########################################################
export interface IFileDropTableColumnConfig {
	bodyEnforceCustomTemplate?: boolean;
	bodyStyleClass?: string;
	bodyTextAlign?: 'left' | 'center' | 'right';
	colspan?: number | 'auto';
	headerEnforceCustomTemplate?: boolean;
	headerStyleClass?: string;
	headerTextAlign?: 'left' | 'center' | 'right';
	identifier: FileDropTableColumnIdEnum | string;
	order: number;

}

export interface IFileDropInputConfig {
	acceptedFileTypes?: string[];
	allowMultiFileDrop?: boolean;
	groupId: string;
	maxFilesize?: IFileDropMaxFilesize;
}

export interface IFileDropGroupItem {
	id: string;
	order: number;
}

export interface IFileDropTableConfig {
	tableColumnItemsConfig: IFileDropTableColumnConfig[];
	group?: {
		baseI18nPath: string;
		items: IFileDropGroupItem[];
		showGroups: boolean;
		showGroupsTotalEntries: boolean;
	};

	[ key: string ]: any;
}

export interface IFileDropConfig {
	inputs: IFileDropInputConfig[];
	table: IFileDropTableConfig;
	keepExistingFileDropListOnNewDrops?: boolean;
}

export interface IFileDropMaxFilesize {
	unit: FilesizeShortUnitEnum;
	value: number;
}

export interface IFileDropFileData {
	allowedFilesizeExceeded?: boolean;
	extension?: string;
	file: File;
	fullname: string;
	name: string;
	prettyFilesize?: string;
}

export interface IFileDropError {
	id: FileDropErrorIdEnum | string;
	long: string;
	short: string;
	params?: IKeyValueMap<any>;
}

export interface IFileDropProcess {

	markedAsUploaded: boolean;

	status: FileProcessStatusEnum;

	errorMap: Map<FileDropErrorIdEnum | string, IFileDropError>;

	errorLabel: string;

	errorTooltipsExist: boolean;

	uploadForbidden: boolean;

	uploadProgress: number;
}

export interface FileProcessConditionals {

	deletableItemsExist?: boolean;

	hideErrors?: boolean;

	importInProgress?: boolean;

	readyForImport?: boolean;

	showLoader?: boolean;
}

export interface IFileDropMetadata {
	groupsEntryIndex: number;
	groupsTotalEntries: number;
	isGroupsFirstEntry: number;
	isGroupsLastEntry: number;
}

export interface IFileDrop {

	entity: any;

	fileData: IFileDropFileData;

	groupId: string;

	metadata?: IFileDropMetadata;

	process: IFileDropProcess;

	[ key: string ]: any;
}

export interface IFileDropCallbacks<T extends IFileDrop, Entity> {
	beforeUploadCheckCallback?: ( fileDropList: IFileDrop[] ) => boolean;
	buildFileDropMapCallback?: ( fileDrop: IFileDrop ) => IFileDrop;
	fileUploadSuccessfulFinishedCallback?: ( fileDrop: IFileDrop ) => void;
	fulfillsUploadRequirementsCallback?: ( fileDropList: IFileDrop[] ) => boolean;
	uploadRequestCallback: ( fileDrop: IFileDrop ) => Observable<HttpEvent<Entity>>;
	validateFileDrop?: ( fileDrop: IFileDrop ) => IFileDrop;
}

export interface IFileDropInitData<T extends IFileDrop, Entity> {
	errorBaseTranslationPath: string;
	errorSupportedStatusCodes: number[];
	fileDropCallbacks: IFileDropCallbacks<T, Entity>;
	fileDropConfig: IFileDropConfig;
	successSupportedStatusCodes?: number[];
}
